import { Button } from "primereact/button";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";

import {
  Checkbox,
  DisabledTooltip,
  Dropdown,
  EmptyState,
  ErrorState,
  ForbiddenState,
  Icon,
  LoadingState,
} from "@/ds";
import {
  CATEGORIA_OPCOES,
  CURVAS_COLUNAS,
  CURVAS_DESCRICAO,
  CURVAS_EDITAR_LABEL,
  CURVAS_EXPANDIR,
  CURVAS_FILTRO_CATEGORIA_ROTULO,
  CURVAS_FORBIDDEN_MESSAGE,
  CURVAS_LISTA_LABEL,
  CURVAS_LOADING_LABEL,
  CURVAS_MOSTRAR_INATIVAS_LABEL,
  CURVAS_NOVA_LABEL,
  CURVAS_DURANTE_APOIO,
  CURVAS_PRAZO_DA_OBRA,
  CURVAS_RECOLHER,
  CURVAS_TAG_INATIVA,
  CURVAS_TITULO,
  CURVAS_VAZIO_DESCRICAO,
  CURVAS_VAZIO_TITULO,
} from "./curvas.constants";
import styles from "./curvas.module.scss";
import { CurvaDialog } from "./curva-dialog";
import { PreviaMensal } from "./previa-mensal";
import { AplicarReferenciaObrasDialog, ConfirmarSituacaoDialog } from "@/shared/components";
import type { CurvasViewModel } from "./types-curvas";

const INATIVAS_INPUT_ID = "curvas-mostrar-inativas";
const COLUNAS_TOTAL = 9;

export function CurvasView({
  conteudo,
  erro,
  categoria,
  alterarCategoria,
  mostrarInativas,
  alternarInativas,
  linhas,
  recarregar,
  nova,
  acaoPendente,
  fecharAcao,
  concluirAcao,
  situacaoDialogo,
  aplicacaoDialogo,
  erroSituacao,
  mobile,
}: CurvasViewModel) {
  return (
    <section
      className={mobile ? `${styles.tela} ${styles.telaMobile}` : styles.tela}
      aria-label={CURVAS_TITULO}
      data-testid="financeiro-curvas-screen"
    >
      <div className={styles.barra}>
        <div className={styles.filtros}>
          <Dropdown
            className={styles.filtro}
            size="sm"
            value={categoria}
            options={[...CATEGORIA_OPCOES]}
            optionLabel="label"
            optionValue="value"
            ariaLabel={CURVAS_FILTRO_CATEGORIA_ROTULO}
            showClear={false}
            valueTemplate={(opcao) => `${CURVAS_FILTRO_CATEGORIA_ROTULO}: ${opcao?.label ?? ""}`}
            onChange={(evento) => alterarCategoria(evento.value)}
            data-testid="curvas-filtro-categoria"
          />
          <div className={styles.inativas}>
            <Checkbox
              inputId={INATIVAS_INPUT_ID}
              checked={mostrarInativas}
              onChange={alternarInativas}
              data-testid="curvas-mostrar-inativas"
            />
            <label htmlFor={INATIVAS_INPUT_ID}>{CURVAS_MOSTRAR_INATIVAS_LABEL}</label>
          </div>
        </div>
        {nova ? (
          <Button
            type="button"
            className={styles.nova}
            icon={<Icon name="plus" size={14} />}
            label={CURVAS_NOVA_LABEL}
            onClick={nova}
            data-testid="curvas-nova"
          />
        ) : null}
      </div>

      <header className={styles.secao}>
        <h2 className={styles.titulo}>{CURVAS_TITULO}</h2>
        <p className={styles.descricao}>{CURVAS_DESCRICAO}</p>
      </header>

      {erroSituacao ? (
        <Message
          className="p-message-block"
          severity="error"
          text={erroSituacao}
          data-testid="curvas-erro-situacao"
        />
      ) : null}

      {conteudo === "carregando" ? (
        <LoadingState label={CURVAS_LOADING_LABEL} testId="curvas-loading" />
      ) : null}
      {conteudo === "sem-permissao" ? (
        <ForbiddenState description={CURVAS_FORBIDDEN_MESSAGE} testId="curvas-sem-permissao" />
      ) : null}
      {conteudo === "erro" ? (
        <ErrorState message={erro ?? undefined} onRetry={recarregar} testId="curvas-erro" />
      ) : null}
      {conteudo === "vazio" ? (
        <EmptyState.Root size="sm" testId="curvas-vazio">
          <EmptyState.Illustration name="sem-dados" />
          <EmptyState.Title>{CURVAS_VAZIO_TITULO}</EmptyState.Title>
          <EmptyState.Description>{CURVAS_VAZIO_DESCRICAO}</EmptyState.Description>
          {nova ? (
            <EmptyState.Action
              label={CURVAS_NOVA_LABEL}
              icon="plus"
              onClick={nova}
              data-testid="curvas-vazio-nova"
            />
          ) : null}
        </EmptyState.Root>
      ) : null}

      {conteudo === "lista" ? (
        <div className={styles.lista}>
          <table
            className={styles.tabela}
            aria-label={CURVAS_LISTA_LABEL}
            data-testid="curvas-tabela"
          >
            <colgroup>
              <col className={styles.colToggle} />
              <col className={styles.colNome} />
              <col className={styles.colCategoria} />
              <col className={styles.colAntes} />
              <col className={styles.colDurante} />
              <col className={styles.colApos} />
              <col className={styles.colMeses} />
              <col className={styles.colAlteradaEm} />
              <col className={styles.colAcoes} />
            </colgroup>
            <thead>
              <tr>
                <th className={styles.colunaToggle} aria-hidden="true" />
                <th scope="col">{CURVAS_COLUNAS.nome}</th>
                <th scope="col">{CURVAS_COLUNAS.categoria}</th>
                <th scope="col">{CURVAS_COLUNAS.antes}</th>
                <th scope="col">{CURVAS_COLUNAS.durante}</th>
                <th scope="col">{CURVAS_COLUNAS.apos}</th>
                <th scope="col">{CURVAS_COLUNAS.meses}</th>
                <th scope="col">{CURVAS_COLUNAS.alteradaEm}</th>
                <th className={styles.colunaAcoes} aria-hidden="true" />
              </tr>
            </thead>
            {linhas.map((linha) => (
              <tbody key={linha.id} className={linha.inativa ? styles.inativa : undefined}>
                <tr data-testid={linha.testId}>
                  <td className={styles.colunaToggle}>
                    <Button
                      type="button"
                      icon={
                        <Icon name={linha.expandida ? "chevron-down" : "chevron-right"} size={14} />
                      }
                      aria-label={`${linha.expandida ? CURVAS_RECOLHER : CURVAS_EXPANDIR} ${linha.nome}`}
                      aria-expanded={linha.expandida}
                      aria-controls={linha.previaId}
                      onClick={linha.alternar}
                      severity="secondary"
                      text
                      rounded
                      data-testid={linha.toggleTestId}
                    />
                  </td>
                  <th scope="row" className={styles.nome}>
                    {linha.nome}
                    {linha.inativa ? <Tag value={CURVAS_TAG_INATIVA} severity="secondary" /> : null}
                  </th>
                  <td>{linha.categoria}</td>
                  <td>{linha.antes}</td>
                  <td>
                    <span className={styles.empilhado}>
                      {linha.durante}
                      <span className={styles.apoio}>{CURVAS_DURANTE_APOIO}</span>
                    </span>
                  </td>
                  <td>{linha.apos}</td>
                  <td>
                    <span className={styles.empilhado}>
                      {CURVAS_PRAZO_DA_OBRA}
                      {linha.mesesExtras ? (
                        <span className={styles.apoio}>{linha.mesesExtras}</span>
                      ) : null}
                    </span>
                  </td>
                  <td>
                    <span className={styles.empilhado}>
                      <span className={styles.alteradaEm}>{linha.alteradaEm}</span>
                      <span className={styles.alteradaPor}>{linha.alteradaPor}</span>
                    </span>
                  </td>
                  <td className={styles.colunaAcoes}>
                    {linha.editar ? (
                      <Button
                        type="button"
                        icon={<Icon name="pencil" size={14} />}
                        aria-label={`${CURVAS_EDITAR_LABEL} ${linha.nome}`}
                        aria-haspopup="dialog"
                        onClick={linha.editar}
                        severity="secondary"
                        text
                        data-testid={linha.editarTestId}
                      />
                    ) : null}
                    {linha.situacao ? (
                      <DisabledTooltip content={linha.situacao.tooltip}>
                        <Button
                          type="button"
                          icon={<Icon name="power-off" size={14} />}
                          aria-label={linha.situacao.rotulo}
                          disabled={linha.situacao.desabilitado}
                          onClick={linha.situacao.executar}
                          severity="secondary"
                          text
                          data-testid={linha.situacao.testId}
                        />
                      </DisabledTooltip>
                    ) : null}
                  </td>
                </tr>
                {linha.expandida ? (
                  <tr>
                    <td colSpan={COLUNAS_TOTAL} className={styles.celulaPrevia}>
                      <div className={styles.previaLinha}>
                        <PreviaMensal nome={linha.nome} fases={linha.previa} id={linha.previaId} />
                      </div>
                    </td>
                  </tr>
                ) : null}
              </tbody>
            ))}
          </table>
        </div>
      ) : null}

      {situacaoDialogo ? (
        <ConfirmarSituacaoDialog
          {...situacaoDialogo.dialogo}
          corpo={
            <>
              <p className={styles.intro}>{situacaoDialogo.intro}</p>
              <div className={styles.resumoCurva} data-testid="curva-situacao-resumo">
                <span className={styles.resumoTopo}>
                  <span className={styles.resumoNome}>{situacaoDialogo.nome}</span>
                  <Tag value={situacaoDialogo.categoria} severity="secondary" />
                </span>
                <span className={styles.apoio}>{situacaoDialogo.resumo}</span>
              </div>
            </>
          }
        />
      ) : null}

      {acaoPendente ? (
        <CurvaDialog
          modo={acaoPendente.tipo === "nova" ? "nova" : "edicao"}
          curva={acaoPendente.tipo === "editar" ? acaoPendente.curva : undefined}
          onClose={fecharAcao}
          onConcluido={concluirAcao}
        />
      ) : null}
      {aplicacaoDialogo ? <AplicarReferenciaObrasDialog {...aplicacaoDialogo} /> : null}
    </section>
  );
}
